import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import Logo from "./Logo";
import { Menu, X, Ticket, LayoutDashboard, LogOut, User, Crown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from "./ui/dropdown-menu";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const onLogout = () => { logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-zinc-800/60 bg-[#2B0B15]/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <Logo />

        <nav className="hidden items-center gap-8 md:flex">
          <Link data-testid="nav-events" to="/events" className="text-sm font-medium text-zinc-300 transition hover:text-amber-400">Discover</Link>
          <Link data-testid="nav-organizer" to="/organizer" className="text-sm font-medium text-zinc-300 transition hover:text-amber-400">For Organizers</Link>
          <Link data-testid="nav-my-bookings" to="/my-bookings" className="text-sm font-medium text-zinc-300 transition hover:text-amber-400">My Tickets</Link>
          {user?.role === "admin" && <Link data-testid="nav-owner" to="/owner" className="text-sm font-medium text-punch-400 transition hover:text-punch-500">Owner HQ</Link>}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="user-menu-btn" variant="ghost" className="border border-zinc-800 text-zinc-100 hover:bg-zinc-900 hover:text-amber-400">
                  <User className="mr-2 h-4 w-4" /> {user.name.split(" ")[0]}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 border-zinc-800 bg-[#33101C] text-zinc-100">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user.name}</div>
                  <div className="text-xs font-mono text-zinc-400">{user.role.toUpperCase()}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem data-testid="menu-my-bookings" onClick={() => navigate("/my-bookings")} className="cursor-pointer focus:bg-zinc-900 focus:text-amber-400">
                  <Ticket className="mr-2 h-4 w-4" /> My Bookings
                </DropdownMenuItem>
                {(user.role === "organizer" || user.role === "admin") && (
                  <DropdownMenuItem data-testid="menu-organizer" onClick={() => navigate("/organizer")} className="cursor-pointer focus:bg-zinc-900 focus:text-amber-400">
                    <LayoutDashboard className="mr-2 h-4 w-4" /> Organizer Console
                  </DropdownMenuItem>
                )}
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-owner" onClick={() => navigate("/owner")} className="cursor-pointer focus:bg-zinc-900 focus:text-amber-400">
                    <Crown className="mr-2 h-4 w-4" /> Owner HQ
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem data-testid="menu-logout" onClick={onLogout} className="cursor-pointer text-red-400 focus:bg-zinc-900 focus:text-red-300">
                  <LogOut className="mr-2 h-4 w-4" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button data-testid="nav-login-btn" variant="ghost" onClick={() => navigate("/login")} className="text-zinc-200 hover:text-amber-400">Log in</Button>
              <Button data-testid="nav-signup-btn" onClick={() => navigate("/register")} className="bg-amber-500 font-semibold text-zinc-950 hover:bg-amber-400">Get Started</Button>
            </>
          )}
        </div>

        <button data-testid="mobile-menu-btn" className="md:hidden text-zinc-200" onClick={() => setOpen(!open)}>
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="border-t border-zinc-800/60 bg-[#2B0B15] md:hidden">
          <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-4">
            <Link to="/events" onClick={() => setOpen(false)} className="text-sm text-zinc-200">Discover</Link>
            <Link to="/organizer" onClick={() => setOpen(false)} className="text-sm text-zinc-200">For Organizers</Link>
            <Link to="/my-bookings" onClick={() => setOpen(false)} className="text-sm text-zinc-200">My Tickets</Link>
            {user ? (
              <Button onClick={onLogout} variant="ghost" className="justify-start text-red-400">Log out</Button>
            ) : (
              <div className="flex gap-2 pt-2">
                <Button onClick={() => { setOpen(false); navigate("/login"); }} variant="outline" className="flex-1 border-zinc-700">Log in</Button>
                <Button onClick={() => { setOpen(false); navigate("/register"); }} className="flex-1 bg-amber-500 text-zinc-950 hover:bg-amber-400">Sign up</Button>
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
